import { CSSProperties, forwardRef, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Select, {
  MultiValue,
  Props,
  SingleValue,
  StylesConfig,
} from "react-select";
import i18next from "i18next";
import { get } from "lodash";
import { Option, OptionType } from "../../interface/OptionTypes";

export interface GeneralSelectProps extends Props {
  isMulti?: boolean;
  maxMenuHeight?: number;
  icon?: string;
  iconVisible?: string;
  onChange?: (value: any) => void;
  value?: Option | Option[];
  name: string;
  onInputChange?: (inputValue: string) => void;
  isClearable?: boolean;
  placeholder?: string;
  label?: string;
  options: Option[];
  isDisabled?: boolean;
  isPending?: boolean;
  required?: boolean;
  className?: string;
  height?: string;
  optionsHight?: string;
  defaultValue?: OptionType | OptionType[];
  menuIsOpen?: boolean;
  register?: any;
  errors?: any;
  customControlStyles?: CSSProperties | undefined;
  placeholderCustomStyle?: CSSProperties | undefined;
}

const GeneralSelect = forwardRef<HTMLDivElement, GeneralSelectProps>(
  (props, ref) => {
    const {
      isMulti,
      maxMenuHeight = 260,
      icon,
      iconVisible,
      onChange,
      name,
      onInputChange,
      isClearable = true,
      placeholder,
      optionsHight,
      options,
      isDisabled,
      height,
      menuIsOpen,
      // errors,
      isPending,
      customControlStyles,
      placeholderCustomStyle,
      label,
    } = props;

    const {
      control,
      formState: { errors },
    } = useFormContext();
    const [isFocused, setIsFocused] = useState(false);
    // Add this after your useState declarations
    const isTouchDevice = useMemo(() => {
      return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    }, []);
    const errorMessage = get(errors, name)?.message;
    const customStyles: StylesConfig<Option, boolean> = useMemo(
      () => ({
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
          border: "1px solid #D7DFED",
          outline: "none",
          boxShadow: "none",
          borderRadius: ".625rem",
          overflow: "hidden",
        }),
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 9999,
          direction: i18next?.language?.includes("ar") ? "rtl" : "ltr",
        }),
        container: (provided) => ({
          ...provided,
          padding: "0",
          // height: "3.5rem",
          width: "auto",
          background: "none",
          border: "none",
        }),
        control: (provided, state) => ({
          ...provided,
          cursor: "pointer",
          padding: "2px 8px",
          borderRadius: "0.625rem",
          textAlign: "start",
          width: "100%",
          margin: "0",
          color: "#566B8D",
          background: "white !important",
          minHeight: "44px !important",
          border: "1px solid #D7DFED !important",
          ...customControlStyles,
        }),
        option: (provided, state) => ({
          ...provided,
          textAlign: "start",
          padding: "12px 16px",
          color: "#566B8D",
          cursor: "pointer",
          backgroundColor: state.isSelected
            ? "#f8faff"
            : state.isFocused
              ? "#f8faff"
              : "transparent",
          ":hover": {
            backgroundColor: "#f8faff",
          },
          ":active": {
            backgroundColor: "transparent",
          },
        }),
        menuList: (provided) => ({
          ...provided,
          borderRadius: "0",
          padding: 0,
          maxHeight: optionsHight || "25rem",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }),
        indicatorSeparator: (provided) => ({
          ...provided,
          display: "none",
          backgroundColor: "transparent",
        }),
        singleValue: (provided, state) => ({
          ...provided,
          color: "#566B8D",
        }),
        multiValue: (provided, state) => ({
          ...provided,
          background: "#EFEFF7",
        }),
        placeholder: (provided) => ({
          ...provided,
          paddingInlineStart: "4px",
          color: "#3F4869 !important",
          ...placeholderCustomStyle,
        }),
      }),
      [],
    );

    const handleChange = (
      selectedOption: MultiValue<OptionType> | SingleValue<OptionType> | null,
    ) => {
      if (onChange) {
        onChange(selectedOption);
      }
    };
    return (
      <div className="w-full">
        {icon && (
          <img className={`select-icon ${iconVisible}`} src={icon} alt="icon" />
        )}

        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            return (
              <div className="relative w-full max-w-[95dvw]">
                <Select
                  {...field}
                  {...props}
                  isMulti={isMulti}
                  menuShouldScrollIntoView
                  menuIsOpen={menuIsOpen}
                  maxMenuHeight={maxMenuHeight}
                  isClearable={isClearable}
                  placeholder={placeholder}
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  menuShouldBlockScroll={!isTouchDevice}
                  options={options}
                  onInputChange={onInputChange}
                  closeMenuOnScroll={true}
                  blurInputOnSelect={true}
                  styles={customStyles as any}
                  isDisabled={isDisabled}
                  isLoading={isPending}
                  onChange={(selectedOption) => {
                    handleChange(selectedOption as MultiValue<OptionType>);
                    if (isMulti) {
                      field.onChange(selectedOption as MultiValue<OptionType>);
                    } else {
                      field.onChange(
                        (selectedOption as SingleValue<OptionType>)?.value ??
                        null,
                      );
                    }
                  }}
                  value={
                    isMulti
                      ? (field.value as MultiValue<OptionType>)
                      : (options?.find(
                        (option) => `${option.value}` == `${field.value}`,
                      ) ?? null)
                  }
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => {
                    field.onBlur();
                    setIsFocused(false);
                  }}
                />
                {field.value != null && (
                  <label
                    htmlFor={name}
                    className={`${isFocused ? "!text-primary" : ""} pointer-events-none absolute start-3 top-0 z-10 -translate-y-1/2 bg-white px-1 text-sm text-primary-text transition-all duration-200`}
                  >
                    {label ?? placeholder}
                  </label>
                )}
              </div>
            );
          }}
        />

        {/* {errors && errors[name]?.message && (
          <span className="error">{errors[name]?.message as string}</span>
        )} */}
        {errorMessage && (
          <span className="error text-xs text-error">
            {errorMessage as string}
          </span>
        )}
      </div>
    );
  },
);

export default GeneralSelect;
